import apiClient from "./apiClient";
import { formatApiError } from "../lib/laravelError";
import { haversineKm } from "../lib/geo";
import { SEED_PLACES, mockDelay } from "./storage";

const MAX_RADIUS_METERS = 100_000;
const MAX_PER_PAGE = 5000;

function normalizePlace(raw) {
  if (!raw || typeof raw !== "object") return null;
  const lat =
    raw.latitude != null
      ? Number(raw.latitude)
      : raw.lat != null
        ? Number(raw.lat)
        : null;
  const lng =
    raw.longitude != null
      ? Number(raw.longitude)
      : raw.lng != null
        ? Number(raw.lng)
        : null;
  const distanceMeters =
    raw.distance != null
      ? Number(raw.distance)
      : raw.distance_meters != null
        ? Number(raw.distance_meters)
        : raw.distanceMeters != null
          ? Number(raw.distanceMeters)
          : null;

  return {
    uuid: raw.uuid ?? raw.id ?? null,
    hotelId: raw.hotel_id ?? raw.hotelId ?? null,
    name: raw.name != null ? String(raw.name) : "",
    url: raw.url != null ? String(raw.url) : null,
    streetAddress: raw.street_address ?? raw.streetAddress ?? null,
    addressLocality: raw.address_locality ?? raw.addressLocality ?? null,
    addressRegion: raw.address_region ?? raw.addressRegion ?? null,
    postalCode: raw.postal_code ?? raw.postalCode ?? null,
    addressCountry: raw.address_country ?? raw.addressCountry ?? "ES",
    category: raw.category != null ? String(raw.category) : null,
    scraped: Boolean(raw.scraped),
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
    distanceKm:
      Number.isFinite(distanceMeters) ? distanceMeters / 1000 : null,
  };
}

function pickPlaceList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const arr = data.data ?? data.places ?? data.items;
    return Array.isArray(arr) ? arr : [];
  }
  return [];
}

function radiusKmToMeters(radiusKm) {
  const km = Math.min(Math.max(Number(radiusKm) || 100, 1), 100);
  return Math.min(Math.round(km * 1000), MAX_RADIUS_METERS);
}

function listNearbyMock({ latitude, longitude, radiusKm }) {
  const capKm = Math.min(Math.max(Number(radiusKm) || 25, 1), 100);
  return SEED_PLACES.map((p) => {
    const place = normalizePlace(p);
    if (!place || place.lat == null || place.lng == null) return null;
    const distanceKm = haversineKm(
      { lat: latitude, lng: longitude },
      { lat: place.lat, lng: place.lng }
    );
    const distanceMeters = Math.round(distanceKm * 1000);
    return { ...place, distanceKm, distanceMeters };
  })
    .filter((p) => p && p.distanceKm <= capKm)
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

export function formatPlaceDistance(place) {
  const meters = place?.distanceMeters;
  if (meters == null || !Number.isFinite(meters)) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/** Dirección completa para el campo privado del encuentro. */
export function formatPlaceAddress(place) {
  if (!place) return "";
  return [
    place.streetAddress,
    place.postalCode,
    place.addressLocality,
    place.addressRegion,
    place.addressCountry,
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatPlaceCategoryLabel(category) {
  if (category == null || String(category).trim() === "") return null;
  const num = Number(category);
  if (!Number.isFinite(num) || num <= 0) return String(category);
  const label = Number.isInteger(num) ? String(num) : String(num);
  return `${label} ${num === 1 ? "estrella" : "estrellas"}`;
}

export function formatPlaceCategoryStars(category) {
  if (category == null || String(category).trim() === "") return null;
  const num = Math.round(Number(category));
  if (!Number.isFinite(num) || num <= 0) return null;
  const filled = Math.min(Math.max(num, 1), 5);
  return `${"★".repeat(filled)}${"☆".repeat(5 - filled)}`;
}

export const placeService = {
  /**
   * GET /lugares — hoteles dentro del radio de acción (metros).
   */
  async listNearby({
    latitude,
    longitude,
    lat,
    lng,
    radiusKm = 25,
    search = "",
    perPage = 20,
    forMap = false,
  }) {
    const latNum = latitude ?? lat;
    const lngNum = longitude ?? lng;
    if (typeof latNum !== "number" || typeof lngNum !== "number") {
      throw new Error("Ubicación no disponible");
    }

    const params = {
      latitude: latNum,
      longitude: lngNum,
      radius: radiusKmToMeters(radiusKm),
    };
    const q = String(search || "").trim();
    if (q) params.search = q;
    if (forMap) {
      params.for_map = true;
    } else {
      params.per_page = Math.min(
        Math.max(Number(perPage) || 20, 1),
        MAX_PER_PAGE
      );
    }

    try {
      const { data } = await apiClient.get("/lugares", { params });
      return pickPlaceList(data)
        .map(normalizePlace)
        .filter((p) => p && p.lat != null && p.lng != null)
        .sort((a, b) => (a.distanceMeters ?? 0) - (b.distanceMeters ?? 0));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 501) {
        await mockDelay(120);
        return listNearbyMock({ latitude: latNum, longitude: lngNum, radiusKm });
      }
      throw new Error(formatApiError(err, "No se pudieron cargar los lugares"));
    }
  },
};
